import { api, ApiError } from './api.js';

/** The file types the API accepts as LLM source documents (PRD sources, imported task lists) —
 * mirrors apps/api/src/llm/prdSourceMime.ts. */
export const SOURCE_FILE_ACCEPT = '.pdf,.md,.txt,.csv,.png,.jpg,.jpeg,.webp';

/** Which upload-url route signs the upload — each kind of source has its own Storage folder. */
export type SourceUploadTarget = 'prd' | 'tasks' | 'training/sources';

/** Uploads one source document and returns its storage path, which the extract, brainstorm and
 * task-import endpoints take in place of the file itself. */
export async function uploadSourceFile(
  projectId: string,
  target: SourceUploadTarget,
  file: File,
): Promise<string> {
  const { uploadUrl, storagePath, contentType } = await api.post<{
    uploadUrl: string;
    storagePath: string;
    contentType: string;
  }>(`/projects/${projectId}/${target}/upload-url`, { filename: file.name });
  // The server decides the type from the extension and signs the URL for it, so the upload
  // must use that type rather than the browser's guess.
  const upload = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  });
  if (!upload.ok) throw new Error(`Upload of ${file.name} failed (${upload.status})`);
  return storagePath;
}

/** A user-facing message for a failed upload. An unsupported file type comes back as a 400 whose
 * message lists the accepted types. */
export function uploadErrorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.status === 400 ? err.message : fallback;
}
